"use client";

import { useMemo, useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  TURKISH_CASE_STRINGS,
  TurkishCaseToggle,
  useTurkishCase,
} from "@/components/tools/turkish-case-toggle";
import { writeToClipboard } from "@/lib/clipboard";
import { parseIntegerInput } from "@/lib/parse-number";
import { foldLower } from "@/lib/turkish-case";

/* ------------------------------------------------------------------ */
/*  Module-level tokenizer — keeps internal apostrophes and hyphens,  */
/*  strips surrounding punctuation.                                   */
/* ------------------------------------------------------------------ */

/*
 * Letters, digits and combining marks in any script, so "café", "résumé", "naïve",
 * "Привет" and Devanagari words (whose vowel signs are marks) stay whole. The old
 * ASCII-only class cut "café" to "caf" and "résumé" to "r" and "sum". A single
 * apostrophe or hyphen is part of the word only between two word characters.
 */
const RE_WORD = /[\p{L}\p{N}\p{M}]+(?:['’\u2010\u2011-][\p{L}\p{N}\p{M}]+)*/gu;

type OutputFormat = "lines" | "comma";

/* ------------------------------------------------------------------ */
/*  Extraction                                                        */
/* ------------------------------------------------------------------ */

export interface ExtractionOptions {
  caseInsensitive: boolean;
  minLength: number;
  showCounts: boolean;
  format: OutputFormat;
  /** Turkish casing for the folded words the reader gets back. */
  turkish: boolean;
  /** The reader's locale, for the alphabetical order of the result. */
  locale: string;
}

interface ExtractionResult {
  totalWords: number;
  uniqueWords: number;
  output: string;
}

function collectCounts(
  text: string,
  caseInsensitive: boolean,
  turkish: boolean
): { counts: Map<string, number>; total: number } {
  const counts = new Map<string, number>();
  let total = 0;
  for (const match of text.matchAll(RE_WORD)) {
    // The folded word is what the reader gets back, not just a bucket key, so it
    // follows the casing rules they picked.
    const word = caseInsensitive ? foldLower(match[0], turkish) : match[0];
    counts.set(word, (counts.get(word) ?? 0) + 1);
    total += 1;
  }
  return { counts, total };
}

export function extractUniqueWords(
  text: string,
  options: ExtractionOptions
): ExtractionResult | null {
  if (text.trim().length === 0) {
    return null;
  }

  const { counts, total } = collectCounts(
    text,
    options.caseInsensitive,
    options.turkish
  );
  const entries = Array.from(counts.entries())
    .filter(([word]) => word.length >= options.minLength)
    .sort((a, b) =>
      a[0].localeCompare(b[0], options.locale, { sensitivity: "base" })
    );

  const items = entries.map(([word, count]) => {
    if (options.showCounts) {
      return `${word} (${count})`;
    }
    return word;
  });
  const separator = options.format === "lines" ? "\n" : ", ";

  return {
    totalWords: total,
    uniqueWords: entries.length,
    output: items.join(separator),
  };
}

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  English strings. A translated content file overrides these        */
/*  through `ui`.                                                     */
/* ------------------------------------------------------------------ */

export const UNIQUE_WORD_EXTRACTOR_STRINGS = {
  ...TURKISH_CASE_STRINGS,
  inputAriaLabel: "Text to extract unique words from",
  inputPlaceholder:
    "Paste or type your text here to extract its unique words...",
  options: "Options",
  caseInsensitive: "Case-insensitive",
  showCounts: "Show counts",
  minLength: "Min length",
  onePerLine: "One per line",
  commaSeparated: "Comma-separated",
  totalWords: "Total Words",
  uniqueWords: "Unique Words",
  outputLabel: "Unique words — alphabetized",
  copied: "Copied!",
  copy: "Copy",
} as const;

/* ------------------------------------------------------------------ */
/*  Widget Component                                                  */
/* ------------------------------------------------------------------ */

export default function UniqueWordExtractorWidget() {
  const strings = useToolUi(UNIQUE_WORD_EXTRACTOR_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [caseInsensitive, setCaseInsensitive] = useState(true);
  const [minLength, setMinLength] = useState(1);
  const [showCounts, setShowCounts] = useState(false);
  const [format, setFormat] = useState<OutputFormat>("lines");
  const [copied, setCopied] = useState(false);
  const [turkish, setTurkish] = useTurkishCase();
  const locale = useToolLocale();

  const result = useMemo(
    () =>
      extractUniqueWords(text, {
        caseInsensitive,
        minLength,
        showCounts,
        format,
        turkish,
        locale,
      }),
    [text, caseInsensitive, minLength, showCounts, format, turkish, locale]
  );

  function handleCopy() {
    if (!result || result.output.length === 0) {
      return;
    }
    writeToClipboard(result.output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleMinLengthChange(raw: string) {
    const parsed = parseIntegerInput(raw);
    if (parsed === null) {
      setMinLength(1);
      return;
    }
    setMinLength(Math.max(1, Math.min(20, parsed)));
  }

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.inputAriaLabel}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.inputPlaceholder}
        value={text}
      />

      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.options}</p>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
            <input
              checked={caseInsensitive}
              className="accent-primary"
              onChange={(e) => setCaseInsensitive(e.target.checked)}
              type="checkbox"
            />
            {strings.caseInsensitive}
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
            <input
              checked={showCounts}
              className="accent-primary"
              onChange={(e) => setShowCounts(e.target.checked)}
              type="checkbox"
            />
            {strings.showCounts}
          </label>
          <label className="flex items-center gap-2 text-foreground text-sm">
            {strings.minLength}
            <input
              className="w-16 rounded-lg border border-border bg-card p-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              max={20}
              min={1}
              onChange={(e) => handleMinLengthChange(e.target.value)}
              type="number"
              value={minLength}
            />
          </label>
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
              <input
                checked={format === "lines"}
                className="accent-primary"
                name="output-format"
                onChange={() => setFormat("lines")}
                type="radio"
                value="lines"
              />
              {strings.onePerLine}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
              <input
                checked={format === "comma"}
                className="accent-primary"
                name="output-format"
                onChange={() => setFormat("comma")}
                type="radio"
                value="comma"
              />
              {strings.commaSeparated}
            </label>
          </div>
        </div>
        <TurkishCaseToggle
          checked={turkish}
          onChange={setTurkish}
          strings={strings}
        />
      </section>

      {/* Stats */}
      {result && (
        <div className="grid grid-cols-2 gap-4">
          <StatCard label={strings.totalWords} value={fmt(result.totalWords)} />
          <StatCard
            label={strings.uniqueWords}
            value={fmt(result.uniqueWords)}
          />
        </div>
      )}

      {/* Output */}
      {result && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="unique-output"
            >
              {strings.outputLabel}
            </label>
            <button
              className="rounded-lg border border-border bg-card px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
              disabled={result.output.length === 0}
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copy}
            </button>
          </div>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="unique-output"
            readOnly
            value={result.output}
          />
        </div>
      )}
    </>
  );
}
